<template>
    <div class="page tours-page py-2 py-lg-6">
        <v-container :class="{'pa-0': phoneOnly}">
            <v-card class="pa-0 transparent" flat>
                <v-card-text class="py-lg-2 py-0" :class="{'px-0': phoneOnly}">
                    <template v-if="!loading">
                        <!-- Tabs appear only once the student has tickets; same tab style as the lecture page. -->
                        <template v-if="bookings.length">
                            <v-divider class="mt-4"></v-divider>
                            <v-tabs v-model="tab" color="primary" light grow class="px-0 mx-0 rounded">
                                <v-tab v-haptic href="#tours" style="letter-spacing: 0">
                                    <v-icon right>mdi-map-marker-multiple-outline</v-icon>
                                    الجولات المتاحة
                                </v-tab>
                                <v-tab v-haptic href="#tickets" style="letter-spacing: 0">
                                    <v-icon right>mdi-ticket-confirmation-outline</v-icon>
                                    تذاكري{{ bookingsByStatus.upcoming.length ? ` (${bookingsByStatus.upcoming.length})` : '' }}
                                </v-tab>
                            </v-tabs>
                            <v-divider class="mb-4"></v-divider>
                        </template>

                        <div v-if="tab === 'tickets' && bookings.length" key="tickets" :class="{'px-4': phoneOnly}">
                            <v-alert v-if="!bookingsByStatus.upcoming.length" outlined text type="info" color="blue-grey">
                                <div class="details-text">لا توجد تذاكر قادمة. احجز مقعدك من «الجولات المتاحة».</div>
                            </v-alert>
                            <v-row>
                                <v-col v-for="booking in bookingsByStatus.upcoming" :key="booking.id" :ref="`ticket-${booking.tour.id}`" cols="12" sm="6" lg="4">
                                    <tour-ticket :booking="booking" :class="{'elevation-6': highlighted === booking.tour.id}" />
                                </v-col>
                            </v-row>

                            <template v-if="bookingsByStatus.used.length">
                                <v-subheader class="px-0 mt-4">التذاكر السابقة</v-subheader>
                                <v-row>
                                    <v-col v-for="booking in bookingsByStatus.used" :key="booking.id" :ref="`ticket-${booking.tour.id}`" cols="12" sm="6" lg="4">
                                        <tour-ticket :booking="booking" />
                                    </v-col>
                                </v-row>
                            </template>
                        </div>

                        <div v-else key="tours">
                            <v-subheader v-if="phoneOnly && !bookings.length" class="mt-2">الجولات المتاحة</v-subheader>
                            <div v-if="pcOnly && !bookings.length" class="strong-text--text mb-1" style="font-size: 1.25rem">الجولات المتاحة</div>
                            <div :class="{'px-4': phoneOnly}">
                                <div class="muted--text details-text mb-4">محاضرات حضورية في قاعات بالمحافظات. احجز مقعدك وادخل برمز QR.</div>

                                <v-alert v-if="bookingsByStatus.upcoming.length" outlined text dense type="info" color="primary" icon="mdi-ticket-confirmation-outline" class="details-text">
                                    <div class="d-flex align-center flex-wrap">
                                        لديك {{ bookingsByStatus.upcoming.length === 1 ? 'تذكرة قادمة' : `${bookingsByStatus.upcoming.length} تذاكر قادمة` }}
                                        <v-spacer></v-spacer>
                                        <v-btn small depressed color="primary" @click="tab = 'tickets'">عرض تذاكري</v-btn>
                                    </div>
                                </v-alert>

                                <v-alert v-if="loadError" outlined text type="error" class="mb-0">
                                    <div class="d-flex align-center flex-wrap details-text">
                                        تعذر تحميل الجولات، تأكد من اتصالك بالإنترنت.
                                        <v-spacer></v-spacer>
                                        <v-btn small depressed color="error" class="mt-2 mt-sm-0" @click="loading = true; load(true)">إعادة المحاولة</v-btn>
                                    </div>
                                </v-alert>

                                <template v-else>
                                    <filter-chip-group v-if="governorates.length > 1" v-model="governorate" :items="governorates" label="المحافظة" icon="mdi-map-marker-outline" all-label="كل المحافظات" class="mb-4" />

                                    <v-alert v-if="tours.length && myGovernorate && !governorates.some(gov => gov.value === myGovernorate)" outlined text dense type="info" color="blue-grey" class="details-text">
                                        لا توجد جولة في محافظتك حالياً، يمكنك الحجز في محافظة أخرى
                                    </v-alert>

                                    <v-alert v-if="!filteredTours.length" outlined text type="info" color="blue-grey" class="mb-0">
                                        <div class="details-text">لا توجد جولات متاحة للحجز حالياً</div>
                                    </v-alert>

                                    <v-row>
                                        <v-col v-for="tour in filteredTours" :key="tour.id" cols="12" sm="6" lg="3">
                                            <v-card outlined class="rounded-lg pa-4 h-100 d-flex flex-column">
                                                <!-- Same title repeats across governorates, so place leads and the title is secondary. -->
                                                <div class="d-flex align-center justify-space-between">
                                                    <div class="font-weight-bold strong-text--text" style="font-size: 1.15rem">
                                                        <v-icon color="primary" class="me-1">mdi-map-marker</v-icon>
                                                        {{ tour.governorate }}
                                                    </div>
                                                    <v-chip v-if="tour.seats_left <= 0 && !tour.is_booked" small label color="error" dark>اكتمل العدد</v-chip>
                                                    <b v-else-if="tour.price <= 0" class="secondary--text">مجانية</b>
                                                    <b v-else-if="platform !== 'ios'" class="primary--text">{{ moneyFormat(tour.price, true) }}</b>
                                                </div>
                                                <div class="strong-text--text mt-1">{{ tour.address }}</div>
                                                <div class="muted--text details-text mb-3">{{ tour.title }}</div>
                                                <div class="strong-text--text font-weight-bold mb-1">
                                                    <v-icon small color="primary" class="me-2">mdi-calendar-blank-outline</v-icon>
                                                    {{ tourDate(tour.starts_at_timestamp) }}
                                                </div>
                                                <div class="text--text">
                                                    <v-icon small color="primary" class="me-2">mdi-clock-outline</v-icon>
                                                    {{ tourTime(tour.starts_at_timestamp) }} - {{ tourTime(tour.ends_at_timestamp) }}
                                                </div>
                                                <v-spacer></v-spacer>
                                                <div class="d-flex align-center justify-space-between my-2" style="min-height: 36px">
                                                    <v-btn v-if="tour.location_url" :href="tour.location_url" :target="capRunning ? '_system' : '_blank'" rel="noopener" text small color="primary" class="px-1">
                                                        <v-icon small left>mdi-map-outline</v-icon>
                                                        افتح الخريطة
                                                    </v-btn>
                                                    <v-spacer></v-spacer>
                                                    <span v-if="!tour.is_booked && tour.seats_left > 0 && tour.seats_left <= 20" class="details-text orange--text text--darken-3">
                                                        المقاعد المتبقية: {{ tour.seats_left }}
                                                    </span>
                                                </div>
                                                <v-btn v-if="tour.is_booked" outlined color="primary" depressed block @click="showTicket(tour.id)">
                                                    <v-icon left>mdi-ticket-confirmation-outline</v-icon>
                                                    عرض التذكرة
                                                </v-btn>
                                                <v-btn v-else-if="tour.seats_left <= 0" depressed block disabled>اكتمل العدد</v-btn>
                                                <v-btn v-else color="primary" depressed block @click="openBooking(tour)">احجز مقعدك</v-btn>
                                            </v-card>
                                        </v-col>
                                    </v-row>
                                </template>
                            </div>
                        </div>
                    </template>
                </v-card-text>
            </v-card>
        </v-container>

        <tour-booking-dialog v-if="selectedTour" v-model="bookingDialog" :tour="selectedTour" @booked="booked" @failed="load(false)" @view-ticket="showTicket(selectedTour.id)" />
    </div>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'
import FilterChipGroup from '@/components/custom/filter-chip-group'
import TourTicket from '@/components/custom/tour-ticket'
import TourBookingDialog from '@/components/custom/tour-booking-dialog'

export default {
    name: 'Tours',
    components: {FilterChipGroup, TourTicket, TourBookingDialog},
    data(){
        return {
            loading: true,
            tab: 'tours',
            loadError: false,
            loadSeq: 0,
            highlighted: null,
            myGovernorate: null,
            tours: [],
            bookings: [],
            governorate: null,
            selectedTour: null,
            bookingDialog: false,
        }
    },
    watch: {
        loading: {
            immediate: true,
            handler(loading){
                this.setLinearLoading(loading);
            },
        },
    },
    computed: {
        ...GlobalComputed,
        governorates(){
            return [...new Set(this.tours.map(tour => tour.governorate))].map(gov => ({text: gov, value: gov}));
        },
        // A ticket is used once the student entered the hall or the tour ended; only upcoming ones need the QR.
        bookingsByStatus(){
            const now = Date.now();
            const byStart = (a, b) => a.tour.starts_at_timestamp - b.tour.starts_at_timestamp;
            const isUsed = booking => !!booking.checked_in_at_timestamp || booking.tour.ends_at_timestamp <= now;
            return {
                upcoming: this.bookings.filter(booking => !isUsed(booking)).sort(byStart),
                used: this.bookings.filter(isUsed).sort(byStart).reverse(),
            };
        },
        filteredTours(){
            return this.governorate ? this.tours.filter(tour => tour.governorate === this.governorate) : this.tours;
        },
    },
    methods: {
        ...GlobalMethods,
        load(firstLoad){
            const seq = ++this.loadSeq; // a slow refresh must not overwrite a booking that landed after it
            this.$store.dispatch('getTours').then(([tours, bookings]) => {
                if (seq !== this.loadSeq){
                    return;
                }
                this.tours = tours.data.data;
                this.myGovernorate = tours.data.my_governorate;
                this.bookings = bookings.data.data;
                this.loadError = false;
                if (this.selectedTour){
                    this.selectedTour = this.tours.find(tour => tour.id === this.selectedTour.id) || this.selectedTour;
                }
                if (firstLoad){
                    // Tours are always the landing view, filtered to the student's governorate.
                    this.governorate = this.tours.some(tour => tour.governorate === this.myGovernorate) ? this.myGovernorate : null;
                }
                this.loading = false;
            }).catch(() => {
                if (seq !== this.loadSeq){
                    return;
                }
                this.loadError = firstLoad || !this.tours.length;
                this.loading = false;
            });
        },
        // Jump to the booked tour's ticket and flash it so the student sees which one it is.
        showTicket(tourId){
            this.tab = 'tickets';
            this.highlighted = tourId;
            this.$nextTick(() => {
                const col = this.$refs[`ticket-${tourId}`];
                if (col && col[0]){
                    this.scrollToElement(col[0]);
                }
                window.setTimeout(() => this.highlighted = null, 2000);
            });
        },
        openBooking(tour){
            this.selectedTour = tour;
            this.$nextTick(() => this.bookingDialog = true);
        },
        booked(booking){
            this.bookings.unshift(booking);
            this.tours = this.tours.map(tour => tour.id === booking.tour.id ? {...tour, is_booked: true, seats_left: booking.tour.seats_left} : tour);
        },
    },
    mounted(){
        if (!this.appData.enable_tours_system){
            this.loading = false;
            this.$router.replace('/not-found');
            return;
        }
        this.load(true);
        this.scrollToTop();
    },
}
</script>

